import {
  BaseMutationOptions,
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import type { ApolloError } from "@apollo/client/core"

import { callMutation } from "shared/api/callMutation"
import {
  DuplicateEntityDocument,
  DuplicateEntityMutation,
  DuplicateEntityMutationVariables
} from "shared/api/duplicate/mutations.generated"
import { evict } from "shared/api/evict"
import {
  evictAffectedPrices,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidateAffectedPrices } from "shared/api/price/modifyPriceCache"
import { useProcess } from "shared/api/process/useProcess"
import { MutationSchema } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProjectParentTypename, RoomTypename } from "shared/types/Project"
import {
  CREATE_PROJECT_CHILD,
  DUPLICATE_ROOM
} from "shared/types/ProjectMutations"
import {
  DuplicateEntityInput,
  Process
} from "shared/types/__generated__/graphql"
import { DuplicateEntityInputSchema } from "shared/types/__generated__/validation"

import { evictProjectChildAndSort } from "entities/project-children/lib/evictProjectChildAndSort"
import { findProjectChildParent } from "entities/project-children/lib/findProjectChildParent"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import { useDuplicationAffix } from "entities/projects/model/useDuplicationAffix"
import { useRoomLazyQuery } from "entities/rooms/graphql/queries.generated"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import z from "zod"

import { readRoomCompleteFragment } from "../lib/readRoomFragment"

const DuplicateEntityVariablesSchema = z.object({
  input: DuplicateEntityInputSchema()
})

export function useDuplicateRoom() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { setElementPending, unsetElementPending } = useProjectStoreActions()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Room.DUPLICATE_ROOM.Start"),
      success: t("Request.Room.DUPLICATE_ROOM.Success"),
      failure: t("Request.Room.DUPLICATE_ROOM.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const handleError = useEvent(
    (
      error: ApolloError,
      options:
        | BaseMutationOptions<
            DuplicateEntityMutation,
            Partial<DuplicateEntityMutationVariables>
          >
        | undefined
    ) => {
      if (options?.variables?.input?.desiredId) {
        evict({
          cache: apolloCache,
          id: options.variables.input.desiredId,
          typename: RoomTypename
        })
      }
    }
  )

  const [apolloMutate, rest] = useApolloMutation<
    DuplicateEntityMutation,
    DuplicateEntityMutationVariables
  >(DuplicateEntityDocument)

  const [fetchRoom] = useRoomLazyQuery()
  const [fetchChildren] = useProjectChildrenLazyQuery()

  const { addSuffix } = useDuplicationAffix()
  const handleProcess = useProcess()

  const handleProcessComplete = useEvent((process: Process) => {
    if (process.copyId) {
      return fetchRoom({
        variables: { id: process.copyId },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
    }
    throw new Error("Process contained no ID for Result!")
  })

  const duplicateRoom = useEvent(
    async ({
      roomID,
      projectID,
      parentID,
      parentType,
      position,
      label,
      onError
    }: {
      roomID: string
      projectID: string
      parentID?: string
      parentType?: ProjectParentTypename
      label?: string
      position?: number
      onError?: (
        error: ApolloError,
        options: BaseMutationOptions | undefined
      ) => void
    }) => {
      await fetchChildren({
        variables: { id: projectID }
      })

      const room = readRoomCompleteFragment(roomID, apolloCache)
      if (!room) {
        throw new Error("Room not found!")
      }
      const parent = findProjectChildParent({
        projectID,
        childID: roomID,
        cache: apolloCache
      })
      const resolvedParentID = parentID || parent?.id
      const resolvedParentType = (parentType ||
        parent?.__typename) as ProjectParentTypename

      if (!resolvedParentID || !resolvedParentType) {
        throw new Error("Parent not found!")
      }

      const id = generateUUID()

      insertProjectChild({
        child: {
          ...room,
          id,
          label: label || addSuffix(room.label || room.number),
          children: [],
          hasClarificationRequest: false
        },
        position,
        parentID: resolvedParentID,
        parentType: resolvedParentType,
        cache: apolloCache
      })

      const input: DuplicateEntityInput = {
        id: generateUUID(),
        label: label || addSuffix(room.label || room.number),
        position,
        recursive: true,
        desiredId: id,
        source: { id: roomID, type: RoomTypename },
        target: { id: resolvedParentID, type: resolvedParentType }
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DUPLICATE_ROOM
      })

      setElementPending({ itemID: id, projectID })
      emitAppEvent(CREATE_PROJECT_CHILD, {
        id,
        projectID,
        parentID: resolvedParentID,
        parentType: resolvedParentType,
        type: RoomTypename
      })
      invalidateAffectedPrices({
        projectID,
        itemID: roomID,
        itemType: RoomTypename,
        includeChild: false,
        cache: apolloCache
      })

      try {
        const mutationResult = await callMutation(apolloMutate, {
          variables: { input },
          transform: data => data.process,
          schemas: {
            input: DuplicateEntityVariablesSchema,
            response: MutationSchema.extend({ process: z.custom<Process>() })
          },
          apolloOptions: {
            onError: (error, options) => {
              handleError(error, options)
              onError?.(error, options)
            }
          }
        })

        const process = mutationResult.data
        if (!process) {
          throw new Error("Duplication returned no Process!")
        }

        const processResult = await handleProcess({
          process,
          onComplete: handleProcessComplete
        })

        raiseSuccessToast()
        evictAffectedPrices({
          projectID,
          itemID: roomID,
          itemType: RoomTypename,
          cache: apolloCache
        })

        return processResult
      } catch (e) {
        evictProjectPrice(projectID, apolloCache, true)
        raiseErrorToast()

        evictProjectChildAndSort({
          id,
          typename: RoomTypename,
          parentID: resolvedParentID,
          parentType: resolvedParentType,
          cache: apolloCache
        })
        throw e
      } finally {
        completeProjectRequest()
        fetchProject({ variables: { id: projectID } })
        unsetElementPending({ itemID: id, projectID })
      }
    }
  )
  return [duplicateRoom, rest] as [typeof duplicateRoom, typeof rest]
}
